// ADR-095 Wave 2.C — per-row JobItem (hover kebab + inline rename).
//
// Verbatim port of the deleted ConversationItem (commit 0d482125^
// services/chat-ui/src/components/history/ConversationSidebar.tsx
// L222-L326) with three substitutions: (a) JobSnapshot prop, (b)
// display-label fallback chain across job.title / truncate(query_text) /
// "Untitled", (c) single JobsKebabMenu replaces three inline IconButtons.
import { useEffect, useRef, useState } from "react";

import { formatRelative } from "@/lib/datetime";
import { deriveDisplayLabel } from "@/lib/jobStatusBadge";
import type { JobSnapshot, JobStatus } from "@/types";

import { JobsKebabMenu } from "./JobsKebabMenu";

interface JobItemProps {
  job: JobSnapshot;
  active?: boolean;
  turnCount?: number;
  onClick: () => void;
  onRename: (newTitle: string) => void;
  onArchive: () => void;
  onDelete: () => void;
}

function statusAccentClass(status: JobStatus): string {
  if (status === "failed" || status === "dead_lettered") return "border-l-[var(--status-error)]";
  if (status === "cancelled") return "border-l-[var(--border-strong)]";
  return "";
}

export function JobItem({
  job,
  active = false,
  turnCount = 1,
  onClick,
  onRename,
  onArchive,
  onDelete,
}: JobItemProps) {
  const [renaming, setRenaming] = useState(false);
  const displayLabel = deriveDisplayLabel(job);
  const [draftTitle, setDraftTitle] = useState(displayLabel);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (renaming) {
      setDraftTitle(displayLabel);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [renaming, displayLabel]);

  function commitRename() {
    const trimmed = draftTitle.trim();
    // Two no-op guards before calling onRename:
    //   (a) empty trimmed — backend Pydantic min_length=1 would HTTP 422
    //       the PATCH, and accidental clear+Enter should silently revert
    //       rather than destroy the user's title.
    //   (b) unchanged — avoids spurious PATCH churn from accidentally
    //       entering rename mode then clicking away (blur commit path).
    if (trimmed.length > 0 && trimmed !== displayLabel) {
      onRename(trimmed);
    }
    setRenaming(false);
  }

  function onRenameKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commitRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setRenaming(false);
    }
  }

  if (renaming) {
    return (
      <div
        className={`rounded-[var(--radius-md)] border-l-2 px-2.5 py-2 ${
          active ? "border-l-[var(--accent-primary)] bg-[var(--accent-soft)]" : "border-l-transparent"
        }`}
      >
        <input
          ref={inputRef}
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          onBlur={commitRename}
          onKeyDown={onRenameKeyDown}
          maxLength={200}
          className="type-body-sm w-full rounded-[var(--radius-sm)] bg-[var(--surface-raised)] px-2 py-1 text-[var(--text-primary)] outline-none ring-1 ring-[var(--accent-primary)] focus-visible:shadow-[var(--elev-focus)]"
          aria-label="Rename thread"
        />
        <div className="type-caption mt-1 text-[var(--text-tertiary)]">
          Enter to save · Esc to cancel
        </div>
      </div>
    );
  }

  const accent = statusAccentClass(job.status);

  return (
    <div
      aria-current={active ? "page" : undefined}
      className={`group relative flex items-stretch rounded-[var(--radius-md)] border-l-2 transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] ${
        active
          ? "border-l-[var(--accent-primary)] bg-[var(--accent-soft)] text-[var(--text-primary)]"
          : "border-l-transparent text-[var(--text-secondary)] hover:border-l-[var(--border-strong)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
      } ${accent}`}
    >
      <button
        type="button"
        onClick={onClick}
        title={displayLabel}
        className="type-body-sm block min-w-0 flex-1 px-2.5 py-2 text-left"
      >
        <div className={`line-clamp-2 leading-snug ${active ? "font-medium" : ""}`}>
          {displayLabel}
        </div>
        <div className="type-caption mt-0.5 flex items-center gap-1.5 text-[var(--text-tertiary)]">
          <span>{formatRelative(job.created_at)}</span>
          {turnCount > 1 && (
            <span
              data-turn-count={turnCount}
              title={`${turnCount} turns in this thread`}
              className="rounded-[var(--radius-pill)] bg-[var(--surface-sunken)] px-1.5 text-[var(--text-secondary)] shadow-[var(--elev-ring)]"
            >
              {turnCount}
            </span>
          )}
        </div>
      </button>
      <div className="flex items-center pr-1.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100">
        <JobsKebabMenu
          onRename={() => setRenaming(true)}
          onArchive={onArchive}
          onDelete={onDelete}
          ariaLabel="Thread options"
        />
      </div>
    </div>
  );
}
